/**
 * Email Confirmation Modal Component
 * Professional replacement for window.prompt() when verifying email on different device
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Mail, Loader2, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function EmailConfirmModal({ isOpen, onConfirm, onCancel }) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    await onConfirm(email);
    setLoading(false);
  };

  const handleClose = () => {
    setEmail('');
    setError('');
    onCancel();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] flex min-h-screen items-center justify-center overflow-y-auto bg-black/50 backdrop-blur-sm p-6 md:p-10"
        onClick={handleClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby="email-confirm-title"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md"
        >
          <Card className="rounded-3xl border border-[rgba(31,76,84,0.12)] shadow-[0_24px_48px_rgba(15,23,42,0.12)]">
            <CardHeader className="space-y-1">
              <div className="flex items-center justify-between">
                <CardTitle id="email-confirm-title" className="text-2xl font-semibold text-[var(--text-primary)]">
                  Confirm your email
                </CardTitle>
                <button
                  onClick={handleClose}
                  className="rounded-full border border-[rgba(31,76,84,0.12)] p-1 text-[var(--text-soft)] transition hover:text-[var(--text-primary)]"
                  aria-label="Close dialog"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
              <CardDescription>
                For security, please confirm the email address you used to sign in.
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email-confirm" className="mb-2 block text-sm font-medium text-[var(--text-primary)]">
                    Email address
                  </label>
                  <input
                    id="email-confirm"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    autoFocus
                    aria-required="true"
                    aria-invalid={error ? "true" : "false"}
                    className="w-full rounded-2xl border border-[rgba(31,76,84,0.16)] bg-white px-4 py-3 text-[var(--text-primary)] transition focus:border-[var(--accent)] focus:ring-4 focus:ring-[rgba(31,76,84,0.12)]"
                  />
                </div>

                {error && (
                  <Alert variant="danger">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    className="flex-1"
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading || !email}
                    size="lg"
                    className="flex-1 justify-center"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <Mail className="mr-2 h-4 w-4" />
                        Confirm
                      </>
                    )}
                  </Button>
                </div>
              </form>

              <p className="mt-4 text-center text-xs text-[var(--text-secondary)]">
                This email should match the one you used when requesting the sign-in link.
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
